import React, { useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardSidebar } from '../components/dashboard/DashboardSidebar';
import { Button } from '../components/ui/Button';
import { CheckIcon, XIcon, CreditCardIcon, CalendarIcon, LockIcon, GlobeIcon, AlertTriangleIcon, ChevronRightIcon, ChevronDownIcon, HelpCircleIcon, UserPlusIcon, StarIcon, SearchIcon, BellIcon, FileTextIcon, MapIcon, BarChartIcon, DownloadIcon, MessageSquareIcon, ShieldIcon, DatabaseIcon, ZapIcon } from 'lucide-react';
// Types
interface PricingPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billingPeriod: 'monthly' | 'annually';
  discountPercent?: number;
  features: {
    included: string[];
    notIncluded?: string[];
  };
  popular?: boolean;
  region: string;
  badge?: string;
  color: string;
  maxUsers: number;
}
interface PaymentMethod {
  id: string;
  type: 'card' | 'paypal' | 'bank_transfer';
  name: string;
  icon: ComponentType<{
    size?: number;
    className?: string;
  }>;
}
// Mock data for pricing plans
const pricingPlans: PricingPlan[] = [{
  id: 'standard-afrique',
  name: 'Standard Afrique',
  description: "Accès essentiel aux appels d'offres africains",
  price: 99,
  currency: '€',
  billingPeriod: 'monthly',
  region: 'Afrique',
  features: {
    included: ['Alertes personnalisées (max 5)', "Accès aux appels d'offres de 10 pays africains", 'Téléchargement documents (limité à 20/mois)', 'Recherche simple', '1 utilisateur'],
    notIncluded: ['Analyse de qualification', 'Assistance premium', 'Utilisateurs multiples', 'Pays supplémentaires']
  },
  color: 'blue',
  maxUsers: 1
}, {
  id: 'premium-afrique',
  name: 'Premium Afrique',
  description: 'Solution complète pour les marchés africains',
  price: 199,
  currency: '€',
  billingPeriod: 'monthly',
  discountPercent: 15,
  region: 'Afrique',
  features: {
    included: ['Alertes personnalisées illimitées', "Accès aux appels d'offres de tous les pays africains", 'Téléchargement documents illimité', 'Recherche avancée avec filtres', 'Analyse de qualification', 'Assistance premium', "Jusqu'à 3 utilisateurs"]
  },
  popular: true,
  badge: 'RECOMMANDÉ',
  color: 'yellow',
  maxUsers: 3
}, {
  id: 'enterprise-afrique',
  name: 'Enterprise Afrique',
  description: 'Solution sur mesure pour les grandes entreprises',
  price: 399,
  currency: '€',
  billingPeriod: 'monthly',
  discountPercent: 20,
  region: 'Afrique',
  features: {
    included: ['Alertes personnalisées illimitées', "Accès prioritaire aux appels d'offres africains", 'Téléchargement documents illimité', 'Recherche avancée avec IA', 'Analyse de qualification approfondie', 'Assistance premium dédiée 24/7', 'Utilisateurs illimités', "API d'intégration", 'Formation personnalisée', 'Rapports sectoriels exclusifs']
  },
  color: 'purple',
  maxUsers: 999
}];
// Payment methods
const paymentMethods: PaymentMethod[] = [{
  id: 'card',
  type: 'card',
  name: 'Carte bancaire',
  icon: CreditCardIcon
}, {
  id: 'paypal',
  type: 'paypal',
  name: 'PayPal',
  icon: GlobeIcon
}, {
  id: 'bank_transfer',
  type: 'bank_transfer',
  name: 'Virement bancaire',
  icon: DatabaseIcon
}];
// FAQ items
const faqItems = [{
  question: 'Puis-je changer de forfait à tout moment ?',
  answer: 'Oui, vous pouvez passer à un forfait supérieur à tout moment. Le changement prend effet immédiatement et nous calculons au prorata le montant restant de votre abonnement actuel.'
}, {
  question: "Comment fonctionne la période d'essai ?",
  answer: "La période d'essai gratuite de 7 jours vous donne accès à toutes les fonctionnalités du forfait Premium Afrique. Aucune carte bancaire n'est requise pour l'essai et vous recevrez une notification avant la fin de votre période d'essai."
}, {
  question: 'Quels pays africains sont couverts par la plateforme ?',
  answer: "Notre plateforme couvre l'ensemble du continent africain avec une attention particulière aux marchés francophones (Maghreb, Afrique de l'Ouest et Centrale). Le forfait Standard donne accès à 10 pays majeurs, tandis que les forfaits Premium et Enterprise couvrent l'intégralité du continent."
}, {
  question: 'Est-ce que je peux annuler mon abonnement ?',
  answer: "Oui, vous pouvez annuler votre abonnement à tout moment depuis votre espace client. L'annulation prendra effet à la fin de votre période de facturation en cours."
}, {
  question: 'Comment ajouter des utilisateurs supplémentaires ?',
  answer: "Selon votre forfait, vous pouvez ajouter des utilisateurs dans la section 'Paramètres > Utilisateurs'. Des frais supplémentaires peuvent s'appliquer au-delà de la limite incluse dans votre forfait."
}];
// Feature icons mapping
const featureIcons: Record<string, ComponentType<{
  size?: number;
  className?: string;
}>> = {
  Alertes: BellIcon,
  Accès: GlobeIcon,
  Téléchargement: DownloadIcon,
  Recherche: SearchIcon,
  Analyse: BarChartIcon,
  Assistance: MessageSquareIcon,
  Utilisateurs: UserPlusIcon,
  API: DatabaseIcon,
  Formation: ZapIcon
};
// Helper function to get the appropriate icon for a feature
const getFeatureIcon = (feature: string): ComponentType<{
  size?: number;
  className?: string;
}> => {
  for (const [key, icon] of Object.entries(featureIcons)) {
    if (feature.includes(key)) {
      return icon;
    }
  }
  return FileTextIcon; // Default icon
};
// Main component
export function UpgradePage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('card');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annually'>('annually');
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const navigate = useNavigate();
  // Mock user data
  const user = {
    name: 'FATHALLAH Mehdi',
    company: 'Tunipages',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    email: 'mehdi.fathallah@tunipages.com',
    phone: '+33 7 53 29 34 19',
    plan: 'Essai Gratuit',
    trialDaysLeft: 7
  };
  const toggleFaq = (id: string) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };
  const annualDiscount = 20; // 20% discount for annual billing
  const calculatePrice = (plan: PricingPlan) => {
    let price = plan.price;
    // Apply annual discount if applicable
    if (billingCycle === 'annually') {
      price = price * 12 * (1 - annualDiscount / 100);
    }
    return price;
  };
  const handleUpgrade = (planId: string) => {
    setSelectedPlan(planId);
    // In a real application, this would navigate to a checkout page or open a payment modal
    console.log(`Selected plan: ${planId}, Payment method: ${selectedPaymentMethod}, Billing cycle: ${billingCycle}`);
  };
  const renderPlanPrice = (plan: PricingPlan) => {
    const price = calculatePrice(plan);
    const monthlyEquivalent = billingCycle === 'annually' ? price / 12 : price;
    return <div data-id="element-4466">
        <div className="flex items-baseline justify-center" data-id="element-4467">
          <span className="text-3xl font-bold" data-id="element-4468">
            {plan.currency}
            {Math.round(price)}
          </span>
          <span className="text-gray-600 ml-1 text-sm" data-id="element-4469">
            /{billingCycle === 'annually' ? 'an' : 'mois'}
          </span>
        </div>
        {billingCycle === 'annually' && <div className="text-sm text-gray-600 text-center mt-1" data-id="element-4470">
            Soit {plan.currency}
            {monthlyEquivalent.toFixed(2)}/mois
          </div>}
        {plan.discountPercent && billingCycle === 'monthly' && <div className="text-sm text-green-600 text-center mt-1" data-id="element-4471">
            Économisez {plan.discountPercent}% avec un abonnement annuel
          </div>}
      </div>;
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-4472">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-gray-800 bg-opacity-50 z-20 md:hidden" onClick={() => setSidebarOpen(false)} data-id="element-4473" />}
      {/* Sidebar */}
      <DashboardSidebar open={sidebarOpen} setOpen={setSidebarOpen} data-id="element-4474" />
      {/* Main content */}
      <div className="md:pl-64" data-id="element-4475">
        {/* Header */}
        <DashboardHeader user={user} onMenuClick={() => setSidebarOpen(true)} data-id="element-4476" />
        {/* Main content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8" data-id="element-4477">
          {/* Alert for trial users */}
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 rounded-md" data-id="element-4478">
            <div className="flex" data-id="element-4479">
              <div className="flex-shrink-0" data-id="element-4480">
                <AlertTriangleIcon className="h-5 w-5 text-yellow-500" data-id="element-4481" />
              </div>
              <div className="ml-3" data-id="element-4482">
                <p className="text-sm text-yellow-700" data-id="element-4483">
                  Votre période d'essai gratuit expire dans{' '}
                  <span className="font-semibold" data-id="element-4484">
                    {user.trialDaysLeft} jours
                  </span>
                  . Choisissez un forfait pour continuer à bénéficier de tous
                  les avantages.
                </p>
              </div>
            </div>
          </div>
          {/* Page header */}
          <div className="mb-8 text-center max-w-3xl mx-auto" data-id="element-4485">
            <motion.h1 className="text-3xl font-bold text-gray-900 mb-3" initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-4486">
              Passez à l'abonnement Premium
            </motion.h1>
            <motion.p className="text-lg text-gray-600" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-4487">
              Accédez à des milliers d'appels d'offres en Afrique, recevez des
              alertes personnalisées et augmentez vos chances de remporter des
              marchés.
            </motion.p>
          </div>
          {/* Billing cycle toggle */}
          <div className="max-w-5xl mx-auto mb-8" data-id="element-4488">
            <div className="flex justify-center items-center space-x-4 bg-white p-2 rounded-lg shadow-sm inline-block mx-auto" data-id="element-4489">
              <button onClick={() => setBillingCycle('monthly')} className={`px-4 py-2 rounded-md text-sm font-medium ${billingCycle === 'monthly' ? 'bg-blue-100 text-blue-800' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-4490">
                Mensuel
              </button>
              <button onClick={() => setBillingCycle('annually')} className={`px-4 py-2 rounded-md text-sm font-medium ${billingCycle === 'annually' ? 'bg-blue-100 text-blue-800' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-4491">
                <span className="flex items-center" data-id="element-4492">
                  Annuel{' '}
                  <span className="ml-1 text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full" data-id="element-4493">
                    -{annualDiscount}%
                  </span>
                </span>
              </button>
            </div>
          </div>
          {/* Pricing plans */}
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4494">
            {pricingPlans.map(plan => <motion.div key={plan.id} className={`bg-white rounded-xl shadow-sm overflow-hidden border ${plan.popular ? 'border-yellow-400 ring-2 ring-yellow-400 ring-opacity-20' : 'border-gray-200'} relative`} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} whileHover={{
            y: -5,
            transition: {
              duration: 0.2
            }
          }} data-id="element-4495">
                {/* Popular badge */}
                {plan.badge && <div className="absolute top-0 right-0" data-id="element-4496">
                    <div className="bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg shadow-sm" data-id="element-4497">
                      {plan.badge}
                    </div>
                  </div>}
                {/* Plan header */}
                <div className={`p-5 border-b border-gray-200 bg-${plan.color}-50`} data-id="element-4498">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-4499">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-4500">
                    {plan.description}
                  </p>
                  {/* Plan price */}
                  <div className="mt-4 mb-2" data-id="element-4501">{renderPlanPrice(plan)}</div>
                </div>
                {/* Plan features */}
                <div className="p-5" data-id="element-4502">
                  <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-4503">
                    Ce qui est inclus :
                  </h4>
                  <ul className="space-y-3 mb-5" data-id="element-4504">
                    {plan.features.included.map((feature, index) => <li key={index} className="flex items-start" data-id="element-4505">
                        <CheckIcon size={18} className="text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-4506" />
                        <span className="text-sm text-gray-600" data-id="element-4507">{feature}</span>
                      </li>)}
                  </ul>
                  {plan.features.notIncluded && plan.features.notIncluded.length > 0 && <>
                        <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-4508">
                          Non inclus :
                        </h4>
                        <ul className="space-y-3 mb-5" data-id="element-4509">
                          {plan.features.notIncluded.map((feature, index) => <li key={index} className="flex items-start" data-id="element-4510">
                              <XIcon size={18} className="text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-4511" />
                              <span className="text-sm text-gray-500" data-id="element-4512">
                                {feature}
                              </span>
                            </li>)}
                        </ul>
                      </>}
                  <Button variant={plan.popular ? 'primary' : 'outline'} fullWidth className={`mt-4 ${plan.popular ? 'shadow-lg shadow-yellow-500/10' : ''}`} onClick={() => handleUpgrade(plan.id)} data-id="element-4513">
                    {selectedPlan === plan.id ? 'Sélectionné' : 'Choisir ce forfait'}
                  </Button>
                </div>
              </motion.div>)}
          </div>
          {/* Features comparison section */}
          <div className="max-w-6xl mx-auto mt-16" data-id="element-4514">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-4515">
              Comparaison détaillée des forfaits
            </h2>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200" data-id="element-4516">
              <div className="overflow-x-auto" data-id="element-4517">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-4518">
                  <thead className="bg-gray-50" data-id="element-4519">
                    <tr data-id="element-4520">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4521">
                        Fonctionnalité
                      </th>
                      {pricingPlans.map(plan => <th key={plan.id} scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4522">
                          {plan.name}
                        </th>)}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-4523">
                    {/* Alertes personnalisées */}
                    <tr data-id="element-4524">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center" data-id="element-4525">
                        <BellIcon size={18} className="text-blue-500 mr-2" data-id="element-4526" />
                        Alertes personnalisées
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4527">
                        Max 5
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4528">
                        Illimitées
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4529">
                        Illimitées
                      </td>
                    </tr>
                    {/* Couverture pays */}
                    <tr data-id="element-4530">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center" data-id="element-4531">
                        <GlobeIcon size={18} className="text-blue-500 mr-2" data-id="element-4532" />
                        Couverture géographique
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4533">
                        10 pays
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4534">
                        Tous les pays africains
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4535">
                        Tous les pays africains
                      </td>
                    </tr>
                    {/* Téléchargement */}
                    <tr data-id="element-4536">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center" data-id="element-4537">
                        <DownloadIcon size={18} className="text-blue-500 mr-2" data-id="element-4538" />
                        Téléchargement documents
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4539">
                        Limité (20/mois)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4540">
                        Illimité
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4541">
                        Illimité
                      </td>
                    </tr>
                    {/* Analyse */}
                    <tr data-id="element-4542">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center" data-id="element-4543">
                        <BarChartIcon size={18} className="text-blue-500 mr-2" data-id="element-4544" />
                        Analyse de qualification
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4545">
                        <XIcon size={18} className="inline text-red-500" data-id="element-4546" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4547">
                        <CheckIcon size={18} className="inline text-green-500" data-id="element-4548" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4549">
                        Approfondie
                      </td>
                    </tr>
                    {/* Recherche */}
                    <tr data-id="element-4550">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center" data-id="element-4551">
                        <SearchIcon size={18} className="text-blue-500 mr-2" data-id="element-4552" />
                        Fonctionnalités de recherche
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4553">
                        Simple
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4554">
                        Avancée avec filtres
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4555">
                        Avancée avec IA
                      </td>
                    </tr>
                    {/* Assistance */}
                    <tr data-id="element-4556">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center" data-id="element-4557">
                        <MessageSquareIcon size={18} className="text-blue-500 mr-2" data-id="element-4558" />
                        Assistance
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4559">
                        Standard
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4560">
                        Premium
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4561">
                        Premium dédiée 24/7
                      </td>
                    </tr>
                    {/* Utilisateurs */}
                    <tr data-id="element-4562">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center" data-id="element-4563">
                        <UserPlusIcon size={18} className="text-blue-500 mr-2" data-id="element-4564" />
                        Utilisateurs
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4565">
                        1
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4566">
                        Jusqu'à 3
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4567">
                        Illimités
                      </td>
                    </tr>
                    {/* Rapports */}
                    <tr data-id="element-4568">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center" data-id="element-4569">
                        <FileTextIcon size={18} className="text-blue-500 mr-2" data-id="element-4570" />
                        Rapports sectoriels
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4571">
                        <XIcon size={18} className="inline text-red-500" data-id="element-4572" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4573">
                        <XIcon size={18} className="inline text-red-500" data-id="element-4574" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4575">
                        <CheckIcon size={18} className="inline text-green-500" data-id="element-4576" />
                      </td>
                    </tr>
                    {/* API */}
                    <tr data-id="element-4577">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center" data-id="element-4578">
                        <DatabaseIcon size={18} className="text-blue-500 mr-2" data-id="element-4579" />
                        API d'intégration
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4580">
                        <XIcon size={18} className="inline text-red-500" data-id="element-4581" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4582">
                        <XIcon size={18} className="inline text-red-500" data-id="element-4583" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-4584">
                        <CheckIcon size={18} className="inline text-green-500" data-id="element-4585" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* Payment information section - shown when a plan is selected */}
          {selectedPlan && <motion.div className="max-w-3xl mx-auto mt-16 bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200" initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} transition={{
          duration: 0.5
        }} data-id="element-4586">
              <div className="p-6 border-b border-gray-200" data-id="element-4587">
                <h2 className="text-xl font-bold text-gray-900" data-id="element-4588">
                  Informations de paiement
                </h2>
                <p className="text-sm text-gray-600 mt-1" data-id="element-4589">
                  Vous avez sélectionné le forfait{' '}
                  <span className="font-medium" data-id="element-4590">
                    {pricingPlans.find(p => p.id === selectedPlan)?.name}
                  </span>{' '}
                  avec facturation{' '}
                  {billingCycle === 'monthly' ? 'mensuelle' : 'annuelle'}.
                </p>
              </div>
              <div className="p-6 space-y-6" data-id="element-4591">
                {/* Payment methods */}
                <div data-id="element-4592">
                  <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-4593">
                    Méthode de paiement
                  </h3>
                  <div className="grid grid-cols-3 gap-3" data-id="element-4594">
                    {paymentMethods.map(method => <button key={method.id} type="button" className={`relative rounded-lg border p-4 flex flex-col items-center ${selectedPaymentMethod === method.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'}`} onClick={() => setSelectedPaymentMethod(method.id)} data-id="element-4595">
                        <method.icon size={24} className={selectedPaymentMethod === method.id ? 'text-blue-600' : 'text-gray-600'} data-id="element-4596" />
                        <span className="mt-2 text-xs font-medium text-gray-900" data-id="element-4597">
                          {method.name}
                        </span>
                        {selectedPaymentMethod === method.id && <div className="absolute top-2 right-2" data-id="element-4598">
                            <CheckIcon size={16} className="text-blue-600" data-id="element-4599" />
                          </div>}
                      </button>)}
                  </div>
                </div>
                {/* Credit card form */}
                {selectedPaymentMethod === 'card' && <div className="space-y-4" data-id="element-4600">
                    <div data-id="element-4601">
                      <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4602">
                        Numéro de carte
                      </label>
                      <div className="relative" data-id="element-4603">
                        <input type="text" id="cardNumber" placeholder="1234 5678 9012 3456" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pl-10" data-id="element-4604" />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4605">
                          <CreditCardIcon size={16} className="text-gray-400" data-id="element-4606" />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-4607">
                      <div data-id="element-4608">
                        <label htmlFor="expiration" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4609">
                          Date d'expiration
                        </label>
                        <div className="relative" data-id="element-4610">
                          <input type="text" id="expiration" placeholder="MM / YY" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pl-10" data-id="element-4611" />
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4612">
                            <CalendarIcon size={16} className="text-gray-400" data-id="element-4613" />
                          </div>
                        </div>
                      </div>
                      <div data-id="element-4614">
                        <label htmlFor="cvc" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4615">
                          CVC
                        </label>
                        <div className="relative" data-id="element-4616">
                          <input type="text" id="cvc" placeholder="123" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm pl-10" data-id="element-4617" />
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-4618">
                            <LockIcon size={16} className="text-gray-400" data-id="element-4619" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-4620">
                      <label htmlFor="cardName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4621">
                        Nom sur la carte
                      </label>
                      <input type="text" id="cardName" placeholder="John Doe" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-4622" />
                    </div>
                  </div>}
                {/* PayPal instructions */}
                {selectedPaymentMethod === 'paypal' && <div className="bg-blue-50 p-4 rounded-lg" data-id="element-4623">
                    <p className="text-sm text-blue-700" data-id="element-4624">
                      Vous serez redirigé vers PayPal pour finaliser votre
                      paiement après avoir cliqué sur "Confirmer l'abonnement".
                    </p>
                  </div>}
                {/* Bank transfer instructions */}
                {selectedPaymentMethod === 'bank_transfer' && <div className="bg-blue-50 p-4 rounded-lg" data-id="element-4625">
                    <p className="text-sm text-blue-700 mb-2" data-id="element-4626">
                      Pour le paiement par virement bancaire, vous recevrez par
                      email nos coordonnées bancaires après avoir cliqué sur
                      "Confirmer l'abonnement".
                    </p>
                    <p className="text-sm text-blue-700" data-id="element-4627">
                      <strong data-id="element-4628">Note:</strong> Votre compte sera activé dès
                      réception du paiement (délai de 1 à 3 jours ouvrés).
                    </p>
                  </div>}
                {/* Billing address */}
                <div data-id="element-4629">
                  <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-4630">
                    Adresse de facturation
                  </h3>
                  <div className="space-y-4" data-id="element-4631">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4632">
                      <div data-id="element-4633">
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4634">
                          Société
                        </label>
                        <input type="text" id="company" defaultValue={user.company} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-4635" />
                      </div>
                      <div data-id="element-4636">
                        <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4637">
                          Numéro de TVA (optionnel)
                        </label>
                        <input type="text" id="vatNumber" placeholder="FR12345678901" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-4638" />
                      </div>
                    </div>
                    <div data-id="element-4639">
                      <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4640">
                        Adresse
                      </label>
                      <input type="text" id="address" placeholder="123 Rue Example" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-4641" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-4642">
                      <div className="col-span-2 md:col-span-1" data-id="element-4643">
                        <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4644">
                          Code postal
                        </label>
                        <input type="text" id="postalCode" placeholder="75001" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-4645" />
                      </div>
                      <div className="col-span-2 md:col-span-1" data-id="element-4646">
                        <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4647">
                          Ville
                        </label>
                        <input type="text" id="city" placeholder="Paris" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-4648" />
                      </div>
                      <div className="col-span-2" data-id="element-4649">
                        <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4650">
                          Pays
                        </label>
                        <select id="country" defaultValue="FR" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-4651">
                          <option value="FR" data-id="element-4652">France</option>
                          <option value="BE" data-id="element-4653">Belgique</option>
                          <option value="CH" data-id="element-4654">Suisse</option>
                          <option value="TN" data-id="element-4655">Tunisie</option>
                          <option value="MA" data-id="element-4656">Maroc</option>
                          <option value="DZ" data-id="element-4657">Algérie</option>
                          <option value="SN" data-id="element-4658">Sénégal</option>
                          <option value="CI" data-id="element-4659">Côte d'Ivoire</option>
                          <option value="CM" data-id="element-4660">Cameroun</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Order summary */}
                <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4661">
                  <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-4662">
                    Récapitulatif de commande
                  </h3>
                  <div className="space-y-2" data-id="element-4663">
                    <div className="flex justify-between text-sm" data-id="element-4664">
                      <span className="text-gray-600" data-id="element-4665">Forfait</span>
                      <span className="text-gray-900 font-medium" data-id="element-4666">
                        {pricingPlans.find(p => p.id === selectedPlan)?.name}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-4667">
                      <span className="text-gray-600" data-id="element-4668">Facturation</span>
                      <span className="text-gray-900 font-medium" data-id="element-4669">
                        {billingCycle === 'monthly' ? 'Mensuelle' : 'Annuelle'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-4670">
                      <span className="text-gray-600" data-id="element-4671">Montant</span>
                      <span className="text-gray-900 font-medium" data-id="element-4672">
                        {pricingPlans.find(p => p.id === selectedPlan)?.currency}
                        {Math.round(calculatePrice(pricingPlans.find(p => p.id === selectedPlan)!))}
                        {billingCycle === 'annually' ? '/an' : '/mois'}
                      </span>
                    </div>
                    {billingCycle === 'annually' && <div className="flex justify-between text-sm text-green-600" data-id="element-4673">
                        <span data-id="element-4674">Économie (20%)</span>
                        <span data-id="element-4675">
                          {pricingPlans.find(p => p.id === selectedPlan)?.currency}
                          {Math.round(pricingPlans.find(p => p.id === selectedPlan)!.price * 12 * 0.2)}
                        </span>
                      </div>}
                    <div className="border-t border-gray-200 pt-2 mt-2" data-id="element-4676">
                      <div className="flex justify-between text-sm font-medium" data-id="element-4677">
                        <span className="text-gray-900" data-id="element-4678">Total</span>
                        <span className="text-blue-600" data-id="element-4679">
                          {pricingPlans.find(p => p.id === selectedPlan)?.currency}
                          {Math.round(calculatePrice(pricingPlans.find(p => p.id === selectedPlan)!))}
                          {billingCycle === 'annually' ? '/an' : '/mois'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Terms and conditions */}
                <div className="flex items-start" data-id="element-4680">
                  <div className="flex items-center h-5" data-id="element-4681">
                    <input id="terms" name="terms" type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-4682" />
                  </div>
                  <div className="ml-3 text-sm" data-id="element-4683">
                    <label htmlFor="terms" className="text-gray-600" data-id="element-4684">
                      J'accepte les{' '}
                      <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-4685">
                        conditions générales
                      </a>{' '}
                      et la{' '}
                      <a href="#" className="text-blue-600 hover:text-blue-800" data-id="element-4686">
                        politique de confidentialité
                      </a>
                    </label>
                  </div>
                </div>
                {/* Submit button */}
                <div className="flex justify-end" data-id="element-4687">
                  <Button variant="primary" size="lg" className="shadow-lg shadow-blue-500/10 px-6" onClick={() => {
                // In a real application, this would submit the payment form
                console.log('Processing payment...');
                navigate('/dashboard');
              }} data-id="element-4688">
                    Confirmer l'abonnement
                  </Button>
                </div>
              </div>
            </motion.div>}
          {/* Trust badges */}
          <div className="max-w-4xl mx-auto mt-16 text-center" data-id="element-4689">
            <h2 className="text-lg font-medium text-gray-900 mb-6" data-id="element-4690">
              Paiement 100% sécurisé
            </h2>
            <div className="flex flex-wrap justify-center items-center gap-8" data-id="element-4691">
              <div className="flex items-center" data-id="element-4692">
                <ShieldIcon size={24} className="text-green-600 mr-2" data-id="element-4693" />
                <span className="text-sm text-gray-600" data-id="element-4694">
                  Paiement sécurisé SSL
                </span>
              </div>
              <div className="h-8 w-16 bg-gray-200 rounded opacity-60" data-id="element-4695"></div>{' '}
              {/* Visa placeholder */}
              <div className="h-8 w-16 bg-gray-200 rounded opacity-60" data-id="element-4696"></div>{' '}
              {/* Mastercard placeholder */}
              <div className="h-8 w-16 bg-gray-200 rounded opacity-60" data-id="element-4697"></div>{' '}
              {/* PayPal placeholder */}
              <div className="h-8 w-16 bg-gray-200 rounded opacity-60" data-id="element-4698"></div>{' '}
              {/* SEPA placeholder */}
            </div>
          </div>
          {/* FAQ section */}
          <div className="max-w-3xl mx-auto mt-16" data-id="element-4699">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-4700">
              Questions fréquentes
            </h2>
            <div className="space-y-4" data-id="element-4701">
              {faqItems.map((item, index) => <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-4702">
                  <button className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none" onClick={() => toggleFaq(`faq-${index}`)} data-id="element-4703">
                    <span className="text-sm font-medium text-gray-900" data-id="element-4704">
                      {item.question}
                    </span>
                    <ChevronDownIcon size={20} className={`text-gray-500 transition-transform ${expandedFaq === `faq-${index}` ? 'transform rotate-180' : ''}`} data-id="element-4705" />
                  </button>
                  {expandedFaq === `faq-${index}` && <div className="px-6 pb-4" data-id="element-4706">
                      <p className="text-sm text-gray-600" data-id="element-4707">{item.answer}</p>
                    </div>}
                </div>)}
            </div>
            <div className="mt-8 text-center" data-id="element-4708">
              <p className="text-gray-600 mb-4" data-id="element-4709">
                Vous avez d'autres questions ?
              </p>
              <Button variant="outline" className="inline-flex items-center" onClick={() => navigate('/contact')} data-id="element-4710">
                <HelpCircleIcon size={18} className="mr-2" data-id="element-4711" />
                Contacter le support
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>;
}